import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, Renderer2
} from '@angular/core';

@Component({
  selector: 'app-bar-guage',
  templateUrl: './bar-guage.component.html',
  styleUrls: ['./bar-guage.component.scss'],
  standalone: false
})
export class BarGuageComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() minValue = 0;
  @Input() maxValue = 100;
  @Input() score?: number | null;
  @Input() previousScore?: number | null;
  @Input() tickStep = 0.2; // default = 20%
  @Input() outerPaddingRatio = 0; // e.g., 0.02 = 2% top & bottom padding

  /** length as % of block height (e.g. 0.25 = 25%) */
  @Input() tickLengthRatio = 0.10;
  /** stroke width in px for mini-lines */
  @Input() tickStrokeWidth = 2;

  @Input() animated = false;
  @Input() animationDuration = 900;

  @Input() invertColours = false;
  @Input() appearance: 'auto' | 'light' | 'dark' = 'auto';

  /** Block geometry */
  @Input() blocks = 16;     // fewer = wider blocks
  @Input() gap = 4;         // px between blocks

  /** How transparent inactive blocks look (0..1; lower = more transparent) */
  @Input() inactiveOpacity = 0.1;

  @Output() infoClick = new EventEmitter<void>();

  segments: { x: number; y: number; w: number; h: number; color: string; opacity: number; index: number }[] = [];
  ticks: { x1: number; y1: number; x2: number; y2: number }[] = [];

  displayedScore?: number | null;
  private animationFrameId: number | null = null;
  private startTime = 0;
  private previousScoreValue?: number | null;

  /** 600x120 viewBox */
  private readonly vbW = 600;
  private readonly vbH = 35;

  /** Bar frame (proportional to height) */
  private readonly barX = 20;
  private readonly barW = 560;
  tickFractions: number[] = [];


  private get barH() {
    const pad = this.outerPaddingRatio * this.vbH;
    const usable = this.vbH - 2 * pad;
    return usable / (1 + 2 * this.tickLengthRatio);
  }
  private get barY() {
    const pad = this.outerPaddingRatio * this.vbH;
    return pad + this.barH * this.tickLengthRatio;
  }

  constructor(private renderer: Renderer2, private el: ElementRef) { }

  ngOnInit() {
    const steps = Math.floor(1 / this.tickStep) - 1; // exclude 0 and 1
    this.tickFractions = Array.from({ length: steps }, (_, i) => (i + 1) * this.tickStep);
    this.applyAppearanceMode();
    this.renderControl();
  }
  ngAfterViewInit() {
    this.renderControl();
  }

  ngOnChanges(): void {
    this.applyAppearanceMode();

    if (this.score === this.previousScoreValue) return;
    this.previousScoreValue = this.score;

    if (this.animated) {
      if (this.animationFrameId !== null) cancelAnimationFrame(this.animationFrameId);
      this.startTime = performance.now();
      this.animate();
    } else {
      this.displayedScore = this.normalizedScore;
      this.renderControl();
    }
  }

  private applyAppearanceMode(): void {
    const host = this.el.nativeElement as HTMLElement;
    const existing = Array.from(host.classList).find(c => (c as string).startsWith('mode-'));
    if (existing) this.renderer.removeClass(host, existing);
    this.renderer.addClass(host, `mode-${this.appearance}`);
  }

  private renderControl() {
    this.buildSegments();
    this.buildTicks();
  }

  /** Build blocks with ghosted opacity for inactive ones */
  private buildSegments() {
    const segWidth = (this.barW - (this.blocks - 1) * this.gap) / this.blocks;
    const activeUpTo = (this.displayedScore ?? 0) / 100 * this.blocks;

    // clamp inactiveOpacity between 0 and 1
    const minOpacity = Math.min(1, Math.max(0, this.inactiveOpacity));

    this.segments = [];
    for (let i = 0; i < this.blocks; i++) {
      const x = this.barX + i * (segWidth + this.gap);
      const y = this.barY;
      const color = this.getColor(i / (this.blocks - 1) * 100);

      // Opacity logic (1 = solid/active, minOpacity = ghosted)
      let opacity = 1;
      const progress = activeUpTo - i;

      if (progress > 0) {
        opacity = 1; // already passed -> fully solid
      } else if (progress > -1) {
        // leading block: smoothly blend from ghosted → solid
        const frac = progress + 1; // 0..1
        opacity = minOpacity + (1 - minOpacity) * frac; // lerp
      } else {
        // future blocks: ghosted
        opacity = minOpacity;
      }

      this.segments.push({ x, y, w: segWidth, h: this.barH, color, opacity, index: i });
    }
  }

  /** Subtle ticks to match fewer blocks */
  private buildTicks() {
    const yTop = this.barY + this.barH + 10;
    const yBot = yTop + 6;
    const segWidth = (this.barW - (this.blocks - 1) * this.gap) / this.blocks;
    this.ticks = [];
    const step = Math.max(1, Math.round(this.blocks / 4));
    for (let i = 0; i <= this.blocks; i += step) {
      const x = this.barX + i * (segWidth + this.gap);
      this.ticks.push({ x1: x, y1: yTop, x2: x, y2: yBot });
    }
  }

  private animate() {
    if (this.normalizedScore === null || this.normalizedScore === undefined) return;

    const startScore = this.displayedScore ?? 0;
    const targetScore = this.normalizedScore;
    if (startScore === targetScore) return;

    const direction = targetScore > startScore ? 1 : -1;
    const range = Math.abs(targetScore - startScore);
    this.startTime = performance.now();

    const step = () => {
      const now = performance.now();
      const t = Math.min((now - this.startTime) / this.animationDuration, 1);
      this.displayedScore = Math.round(startScore + t * direction * range);
      this.renderControl();
      if (t < 1) this.animationFrameId = requestAnimationFrame(step);
    };
    step();
  }

  // ---- Color helpers (red→yellow→green, or inverted) ----
  private getColor(indexPct: number): string {
    if (this.invertColours) {
      if (indexPct <= 15) return this.gradient(indexPct, 0, 15, "#008005", "#02bb09");
      if (indexPct <= 30) return this.gradient(indexPct, 15, 30, "#02bb09", "#89ed8d");
      if (indexPct <= 55) return this.gradient(indexPct, 30, 55, "#89ed8d", "#f3cc00");
      if (indexPct <= 70) return this.gradient(indexPct, 55, 70, "#f3cc00", "#f3a101");
      if (indexPct <= 85) return this.gradient(indexPct, 70, 85, "#f3a101", "#db0000");
      return this.gradient(indexPct, 85, 100, "#db0000", "#9c0000");
    } else {
      if (indexPct <= 15) return this.gradient(indexPct, 0, 15, "#9c0000", "#db0000");
      if (indexPct <= 30) return this.gradient(indexPct, 15, 30, "#db0000", "#f3a101");
      if (indexPct <= 55) return this.gradient(indexPct, 30, 55, "#f3a101", "#f3cc00");
      if (indexPct <= 70) return this.gradient(indexPct, 55, 70, "#f3cc00", "#89ed8d");
      if (indexPct <= 85) return this.gradient(indexPct, 70, 85, "#89ed8d", "#02bb09");
      return this.gradient(indexPct, 85, 100, "#02bb09", "#008005");
    }
  }

  private gradient(index: number, min: number, max: number, startColor: string, endColor: string): string {
    const s = this.hexToRgb(startColor), e = this.hexToRgb(endColor);
    const steps = Math.max(1, max - min);
    const t = Math.min(1, Math.max(0, (index - min) / steps));
    const rgb: [number, number, number] = [
      Math.round(s[0] + (e[0] - s[0]) * t),
      Math.round(s[1] + (e[1] - s[1]) * t),
      Math.round(s[2] + (e[2] - s[2]) * t),
    ];
    return this.rgbToHex(rgb);
  }
  private hexToRgb(hex: string): [number, number, number] {
    const v = parseInt(hex.slice(1), 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  }
  private rgbToHex([r, g, b]: [number, number, number]): string {
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  private get normalizedScore(): number {
    if (this.score == null || this.maxValue === this.minValue) return 0;
    return ((this.score - this.minValue) / (this.maxValue - this.minValue)) * 100;
  }
}